import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Loading from "../../../common/Loading";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import FormLabel from "@material-ui/core/FormLabel";
import TextField from "@material-ui/core/TextField";

import Button from "components/CustomButtons/Button";

import { actionCreators as actionCreatorsAdmin } from "../../../../_actions/adminActions";

const digitsOnly = new RegExp(/^\d+$/);

const useStyles = makeStyles(theme => ({
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  formLabel: {
    display: "block",
    width: "12rem"
  },
  dialogActions: {
    justifyContent: "center"
  }
}));

const ChapterRangeDialog = ({
  ranges,
  open,
  onClose,
  getChapterRanges,
  updateChapterRanges,
  isFetching,
  isFetched,
  isUpdating
}) => {
  const classes = useStyles();
  const [smallChapterFrom, setSmallChapterFrom] = useState(0);
  const [smallChapterTo, setSmallChapterTo] = useState(0);
  const [mediumChapterFrom, setMediumChapterFrom] = useState(0);
  const [mediumChapterTo, setMediumChapterTo] = useState(0);
  const [largeChapterFrom, setLargeChapterFrom] = useState(0);
  const [largeChapterTo, setLargeChapterTo] = useState(0);
  const [isDirty, setDirty] = useState(false);

  useEffect(() => {
    if (!isFetching.CHAPTER_RANGES && !isFetched.CHAPTER_RANGES) {
      getChapterRanges();
    }
  }, []);

  useEffect(() => {
    if (open) {
      setDirty(false);
    }
  }, [open]);

  useEffect(() => {
    if (ranges && ranges.length > 2) {
      setSmallChapterFrom(ranges[0].low);
      setSmallChapterTo(ranges[0].high);
      setMediumChapterFrom(ranges[1].low);
      setMediumChapterTo(ranges[1].high);
      setLargeChapterFrom(ranges[2].low);
      setLargeChapterTo(ranges[2].high);
    }
  }, [ranges]);

  const handleSmallChapterFrom = useCallback(({ target: { value } }) => {
    if (digitsOnly.test(value) || value.length === 0) {
      setSmallChapterFrom(value);
    }
  }, []);

  const handleSmallChapterTo = useCallback(({ target: { value } }) => {
    if (digitsOnly.test(value) || value.length === 0) {
      setSmallChapterTo(value);
    }
  }, []);

  const handleMediumChapterFrom = useCallback(({ target: { value } }) => {
    if (digitsOnly.test(value) || value.length === 0) {
      setMediumChapterFrom(value);
    }
  }, []);

  const handleMediumChapterTo = useCallback(({ target: { value } }) => {
    if (digitsOnly.test(value) || value.length === 0) {
      setMediumChapterTo(value);
    }
  }, []);

  const handleLargeChapterFrom = useCallback(({ target: { value } }) => {
    if (digitsOnly.test(value) || value.length === 0) {
      setLargeChapterFrom(value);
    }
  }, []);

  const handleLargeChapterTo = useCallback(({ target: { value } }) => {
    if (digitsOnly.test(value) || value.length === 0) {
      setLargeChapterTo(value);
    }
  }, []);

  useEffect(() => {
    if (isUpdating.CHAPTER_RANGES) {
      onClose();
    }
  }, [isUpdating]);

  const handleSave = useCallback(() => {
    setDirty(true);
    updateChapterRanges([
      {
        low: smallChapterFrom,
        high: smallChapterTo,
        id: 1
      },
      {
        low: mediumChapterFrom,
        high: mediumChapterTo,
        id: 2
      },
      {
        low: largeChapterFrom,
        high: largeChapterTo,
        id: 3
      }
    ]);
  }, [
    isDirty,
    updateChapterRanges,
    onClose,
    smallChapterFrom,
    smallChapterTo,
    mediumChapterFrom,
    mediumChapterTo,
    largeChapterFrom,
    largeChapterTo
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Chapter Sizes</DialogTitle>
      <DialogContent>
        <Typography variant="body1" className={classes.marginBottom}>
          You can manage Chapter Size Definitions Below (By Member Number)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container justify="center" alignItems="center" spacing={2}>
              <Grid item>
                <FormLabel className={classes.formLabel}>
                  Small Chapter Range
                </FormLabel>
              </Grid>
              <Grid item>
                <TextField
                  error={isDirty && isNaN(smallChapterFrom)}
                  variant="outlined"
                  value={smallChapterFrom}
                  onChange={handleSmallChapterFrom}
                />
              </Grid>
              <Grid item>
                <FormLabel>To</FormLabel>
              </Grid>
              <Grid item>
                <TextField
                  error={isDirty && !smallChapterTo}
                  variant="outlined"
                  value={smallChapterTo}
                  onChange={handleSmallChapterTo}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container justify="center" alignItems="center" spacing={2}>
              <Grid item>
                <FormLabel className={classes.formLabel}>
                  Medium Chapter Range
                </FormLabel>
              </Grid>
              <Grid item>
                <TextField
                  error={isDirty && !mediumChapterFrom}
                  variant="outlined"
                  value={mediumChapterFrom}
                  onChange={handleMediumChapterFrom}
                />
              </Grid>
              <Grid item>
                <FormLabel>To</FormLabel>
              </Grid>
              <Grid item>
                <TextField
                  error={isDirty && !mediumChapterTo}
                  variant="outlined"
                  value={mediumChapterTo}
                  onChange={handleMediumChapterTo}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container justify="center" alignItems="center" spacing={2}>
              <Grid item>
                <FormLabel className={classes.formLabel}>
                  Large Chapter Range
                </FormLabel>
              </Grid>
              <Grid item>
                <TextField
                  error={isDirty && !largeChapterFrom}
                  variant="outlined"
                  value={largeChapterFrom}
                  onChange={handleLargeChapterFrom}
                />
              </Grid>
              <Grid item>
                <FormLabel>To</FormLabel>
              </Grid>
              <Grid item>
                <TextField
                  error={isDirty && !largeChapterTo}
                  variant="outlined"
                  value={largeChapterTo}
                  onChange={handleLargeChapterTo}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions classes={{ root: classes.dialogActions }}>
        {isDirty ? (
          <Loading />
        ) : (
          <>
            <Button color="success" variant="contained" onClick={handleSave}>
              OK
            </Button>
            <Button color="danger" variant="contained" onClick={onClose}>
              Cancel
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = state => ({
  ranges: state.adminReducer.chapterRanges,
  isFetching: state.adminReducer.isFetching,
  isFetched: state.adminReducer.isFetched,
  isUpdating: state.adminReducer.isUpdating
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsAdmin }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ChapterRangeDialog);
